import * as Sentry from "@sentry/browser";
import { useEffect, useMemo, useRef } from "react";

export const hasWebGLSupport = () => {
    return useMemo(() => {
        try {
            const canvas = document.createElement("canvas");
            const gl =
                canvas.getContext("webgl") ||
                (canvas.getContext(
                    "experimental-webgl",
                ) as WebGLRenderingContext | null);
            const supported = !!window.WebGLRenderingContext && !!gl;
            if (gl) {
                const ext = gl.getExtension("WEBGL_lose_context");
                if (ext) {
                    ext.loseContext();
                }
            }
            canvas.remove();
            return supported;
        } catch (error) {
            Sentry.captureException(error);
            return false;
        }
    }, []);
};

interface ReleasableDevice {
    loseDevice?: () => boolean;
}

interface DeckHandle {
    deck?: unknown;
}

/**
 * Releases a deck.gl WebGL context on unmount.
 *
 * `Deck.finalize()` disposes layers and the render loop but never loses the GL
 * context, so unmounted decks hold context slots until GC. Chrome caps a page
 * at ~16 and evicts the oldest — the main map.
 *
 * Usage: `<DeckGL ref={deckRef} {...useDeckDeviceRelease(deckRef)} />`
 */
export const useDeckDeviceRelease = (deckRef: {
    readonly current?: DeckHandle | null;
}) => {
    const deviceRef = useRef<ReleasableDevice | null>(null);

    useEffect(() => {
        // Grab the deck now: React nulls the ref before the cleanup runs.
        const deck = deckRef.current?.deck as
            | { device?: ReleasableDevice | null }
            | undefined;
        return () => {
            // onDeviceInitialized never fires if the deck is finalized before
            // its async device resolves, hence the fallback.
            const device = deviceRef.current ?? deck?.device;
            device?.loseDevice?.();
            deviceRef.current = null;
        };
    }, [deckRef]);

    return {
        onDeviceInitialized: (device: ReleasableDevice) => {
            deviceRef.current = device;
        },
    };
};
